import { useState } from "react";
import { Mail, Phone, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields"); return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent · we'll get back within 24 hours");
      setForm({ fullName: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="text-center max-w-xl mx-auto fade-up mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Say Hello</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Contact Us</h1>
        <p className="text-muted-foreground mt-3">Questions, custom gifting, or a fragrance recommendation — we'd love to hear from you.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <form data-testid="contact-form-container" onSubmit={submit}
          className="lg:col-span-2 p-6 sm:p-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name*" v={form.fullName} onC={(v) => setForm((s) => ({ ...s, fullName: v }))} tid="contact-fullName" />
            <Field label="Email*" type="email" v={form.email} onC={(v) => setForm((s) => ({ ...s, email: v }))} tid="contact-email" />
            <Field label="Phone" v={form.phone} onC={(v) => setForm((s) => ({ ...s, phone: v }))} tid="contact-phone" />
            <Field label="Subject*" v={form.subject} onC={(v) => setForm((s) => ({ ...s, subject: v }))} tid="contact-subject" />
          </div>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Message*</span>
            <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
              rows={5} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm resize-none" />
          </label>
          <Button data-testid="contact-form-submit-button" type="submit" disabled={sending} size="lg" className="rounded-full">
            {sending ? "Sending…" : "Send Message"}
          </Button>
        </form>

        <div className="space-y-4">
          <Info icon={<Mail className="w-4 h-4" />} label="Email" value="hello@candleco.com" />
          <Info icon={<Phone className="w-4 h-4" />} label="Phone" value="+91 98765 43210" />
          <Info icon={<Clock className="w-4 h-4" />} label="Hours" value="Mon – Sat · 10am – 7pm IST" />
          <Info icon={<MapPin className="w-4 h-4" />} label="Studio" value="Bandra West, Mumbai, India" />
          <div className="rounded-2xl overflow-hidden aspect-[4/3] border border-[hsl(var(--border))] bg-candle-cream flex items-center justify-center text-muted-foreground text-sm">
            <div className="text-center px-4">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-candle-terracotta" />
              Visit our tiny studio<br /><span className="text-xs">Bandra West, Mumbai</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, v, onC, type = "text", tid }) => (
  <label className="block">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">{label}</span>
    <input type={type} value={v} onChange={(e) => onC(e.target.value)} data-testid={tid}
      className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-candle-terracotta/40" />
  </label>
);
const Info = ({ icon, label, value }) => (
  <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] flex items-start gap-3">
    <div className="w-9 h-9 rounded-full bg-candle-terracotta/15 text-candle-terracotta flex items-center justify-center">{icon}</div>
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  </div>
);
